<script setup lang="ts">
import type {ProductInterface, ProductRate, ProductRateRequest} from "~~/server/types/Products";
import {storeToRefs} from "pinia";
import type {FormInstance, FormRules} from "element-plus";
import {useMessages} from "~/composables/useMessages";
import {localDateFormat} from "~/services/helpers";

const props = defineProps<{
    product: ProductInterface;
}>();
const current_rate=ref(props.product.rating || 0);
const productsStore=useProductStore();
const userStore=useAuthStore();
const { isAuthenticated, fullName,user } = storeToRefs(userStore);
const { t } = useI18n();
const {show_error,show_success}=useMessages();

const formRateRef = ref<FormInstance>()

const show_rate_form=ref(false);
const formRate=ref({} as ProductRateRequest)
const formatRateText = (rate: number, count: number) =>
    rate > 0
        ? `${rate} ${t('points')} ${t('of')} ${count} ${t('votes')}`
        : t('no_rates_yet');
const rateTextTemplate=ref(formatRateText(
    props.product.rating || 0,
    props.product.rating_count || 0,
));
const avg=ref(props.product.rating || 0);
const setRate=()=>{
    formRate.value.rate=5;
    formRate.value.product_id=props.product.id;
    formRate.value.comment='';
    formRate.value.guest_name=fullName.value || '';
    formRate.value.guest_email=user.value?.email || '';
    show_rate_form.value=true;
}


const loadRate=async(changing=false)=>{
    loading.value=true;
    try {
        list.value = await productsStore.loadRates(props.product.id)
        const total=list.value.length;
        avg.value=list.value.reduce((acc, cur) => acc + cur.rate, 0) / total;
        setRateText(avg.value || 0,total);
      if (changing) current_rate.value=avg.value;
    }
    finally {
        loading.value=false;
    }
}

const setRateText=(rate:number,count:number)=>{
    rateTextTemplate.value = formatRateText(rate || 0, count);
}

const list=ref([] as ProductRate[])
const loading=ref(false);

const rules: FormRules = {
    rate: [
        { required: true, message: t('please_select_rating'), trigger: 'change' },
    ],
    guest_name: [
        { required: true, message: t('first_name_required'), trigger: 'change' },
    ],
    guest_email: [
        { required: true, message: t('email_required'), trigger: 'change' },
        { type: 'email', message: t('email_invalid'), trigger: 'change' },
    ],
}

const save=async () => {
    const valid = await formRateRef.value!.validate().catch(() => false)
    if (!valid) return

    loading.value = true
    try {
        const res=await productsStore.rate(formRate.value)
        show_rate_form.value=false;
        if (res.status===1){
            await show_success(t('rate_success'));
        } else {
            await show_success(t('rate_awaiting_approval'));
        }
        await loadRate(true);
    }
    catch (e) {
      await show_error(e)
    }
    finally {
        loading.value = false
    }
}

onMounted(async () => await loadRate());

</script>

<template>
  <div class="productInfoBlock">
    <div class="title">
      <Icon name="material-symbols-light:star-shine-outline" />
      {{$t('rating')}}
      <el-button type="primary" round class="fs--2 fw-semibold ms-auto" size="small"  @click="setRate">
        {{$t('rate_your')}}
      </el-button>
    </div>
    <div class="description">
      <el-rate v-model="current_rate"  allow-half disabled  aria-label="Rating" style="height: auto" />
      <el-text tag="div" type="info" class="mb-2 fw-bold" size="small" >
          {{rateTextTemplate}}
      </el-text>
    </div>
  </div>
    <el-dialog
            v-model="show_rate_form"
            :title="$t('rate_this_product')"
            width="420px"
            destroy-on-close
    >
      <el-divider class="mb-3 mt-0 " border-style="solid" :style="{ borderColor: 'var(--el-bg-color-page)'}" />
        <el-form
                ref="formRateRef"
                :model="formRate"
                :rules="rules"
                label-position="top"
        >
            <el-form-item :label="$t('rating')" prop="rate">
                <el-rate
                        v-model="formRate.rate"
                        show-text
                        :texts="[
                                $t('very_bad'),
                                $t('bad'),
                                $t('ok'),
                                $t('good'),
                                $t('excellent')
                              ]"
                        :max="5"
                />
            </el-form-item>

            <el-form-item :label="$t('comment')">
                <el-input
                        v-model="formRate.comment"
                        type="textarea"
                        :rows="3"
                        :placeholder="$t('optional_comment')"
                />
            </el-form-item>

            <template v-if="!isAuthenticated">
                <el-form-item :label="$t('name')"  prop="guest_name" >
                    <el-input  v-model="formRate.guest_name" class="custom_inputs" type="name"  />
                </el-form-item>
                <el-form-item :label="$t('email')"  prop="guest_email">
                    <el-input  v-model="formRate.guest_email" class="custom_inputs" type="email" />
                </el-form-item>
            </template>

        </el-form>

        <template #footer>
            <el-button round @click="show_rate_form = false">
                {{ $t('cancel') }}
            </el-button>

            <el-button
                     round
                    type="primary"
                    :loading="loading"
                    @click="save"
            >
                {{ $t('rate') }}
            </el-button>
        </template>
    </el-dialog>

  <teleport v-if="list.length" to="#rate_list">
    <div class="productInfoBlock mb-4">
      <div class="title d-flex justify-content-between align-items-center">
        <div class="title mb-0">
        <Icon name="material-symbols-light:star-shine-outline" /> {{$t('ratesAndComments')}}
        </div>
        <el-button type="primary" round size="small" @click="show_rate_form = true">
          {{$t('rate_your')}}
        </el-button>
      </div>
      <div class="description">
        <el-scrollbar :max-height="250">
          <div v-for="item in list" :key="item.id" class="rating-row mb-3">
        <div class="left">
          <el-text class="fw-bold" size="small">
            {{ item.guest_name || 'Guest' }}
          </el-text>

          <el-text type="info" size="small">
            {{ localDateFormat(item.created_at) }}
          </el-text>

          <el-text v-if="item.comment" class="comment" size="small">
            {{ item.comment}}
          </el-text>
        </div>

        <div class="right">
          <el-rate v-model="item.rate" disabled />
        </div>
      </div>
        </el-scrollbar>
      </div>
    </div>
  </teleport>


</template>

<style scoped lang="scss">
.ratings-compact {
  width: 100%;
}

.rating-row {
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid var(--el-border-color-lighter);
  background: var(--el-fill-color-blank);
}

.rating-row .left {
  flex: 1;
  min-width: 0;
  display: grid;
  gap: 4px;
}

.rating-row .right {
  display: flex;
  align-items: start;
  flex-shrink: 0;
}

.name {
  font-weight: 700;
}

.comment {
  margin-top: 2px;
  line-height: 1.45;
  white-space: pre-line;
  word-break: break-word;
}


</style>
